import { useState } from 'react'
import { AudioPlayerProvider, useAudioPlayer } from 'react-use-audio-player'
import { getSpeech } from '@mote/api'
import { WarningCircleOutlineIcon } from '@mote/assets/icons'
import { Text } from '@mote/ui'
import {
  ErrorControl,
  PlayToteButtonWrapper,
  StyledIconButton,
  StyledSpinner
} from './PlayToteButton.styles'

export interface PlayToteButtonProps {
  toteId: string
  spacing?: 'bottom'
}

type PlayToteButtonState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; src: string }

function _PlayToteButton(props: PlayToteButtonProps): JSX.Element {
  const [playerState, setPlayerState] = useState<PlayToteButtonState>({
    status: 'idle'
  })

  let buttonComponent = <></>
  let text = ''

  if (playerState.status === 'loading') {
    buttonComponent = <StyledSpinner />
    text = 'Loading speech...'
  }

  if (playerState.status === 'idle') {
    buttonComponent = (
      <StyledIconButton
        //@ts-expect-error
        testId={'load-mp3-button'}
        onClick={loadMp3}
      />
    )
    text = 'Click to listen'
  }

  if (playerState.status === 'ready') {
    buttonComponent = <ReadyStatePlayToteButton src={playerState.src} />
    text = 'Click to listen'
  }

  if (playerState.status === 'error') {
    buttonComponent = (
      //@ts-expect-error
      <ErrorControl onClick={loadMp3}>
        <WarningCircleOutlineIcon color={'red'} />
      </ErrorControl>
    )
    text = 'Could not load text to speech, try again later.'
  }

  return (
    <>
      {buttonComponent}
      <Text fontWeight={500} fontSize={12} tag={'small'} color={'#16161D'}>
        {text}
      </Text>
    </>
  )

  async function loadMp3() {
    try {
      setPlayerState({ status: 'loading' })
      const blob = await getSpeech(props.toteId)
      setPlayerState({ status: 'ready', src: URL.createObjectURL(blob) })
    } catch {
      setPlayerState({ status: 'error' })
    }
  }
}

interface ReadyStatePlayToteButtonProps {
  src: string
}

function ReadyStatePlayToteButton(
  props: ReadyStatePlayToteButtonProps
): JSX.Element {
  const audioPlayer = useAudioPlayer({
    src: props.src,
    format: 'mp3',
    html5: true,
    autoplay: true
  })

  return (
    <StyledIconButton
      //@ts-expect-error
      testId={audioPlayer.playing ? 'pause-button' : 'play-button'}
      onClick={() => {
        if (audioPlayer.playing) {
          audioPlayer.player!.seek(0)
        } else {
          audioPlayer.play()
        }
      }}
    />
  )
}

export function PlayToteButton(props: PlayToteButtonProps): JSX.Element {
  return (
    <AudioPlayerProvider>
      <PlayToteButtonWrapper spacing={props.spacing}>
        <_PlayToteButton toteId={props.toteId} />
      </PlayToteButtonWrapper>
    </AudioPlayerProvider>
  )
}
